import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { captureReferral } from './lib/referral';

captureReferral();
import { ThemeProvider } from './contexts/ThemeContext'; // ADDED FOR THEME
import { AuthProvider, ProtectedRoute, PublicOnlyRoute, AdminRoute } from './contexts/AuthContext';
import { OccupationProvider } from './contexts/OccupationContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { MembershipProvider } from './contexts/MembershipContext'; // ADDED FOR TOKEN SYSTEM
import { RewardsProvider } from './contexts/RewardsContext'; // ADDED FOR TOKEN SYSTEM
import { RequireVerifiedEmail } from './components/auth/RequireVerifiedEmail';

// Layouts (named exports → adapt to default for React.lazy)
const OccupationLayout = lazy(() =>
  import('./pages/occupation/OccupationLayout').then((m) => ({ default: m.OccupationLayout })),
);
const CareerLayout = lazy(() => import('./pages/career/CareerLayout').then((m) => ({ default: m.CareerLayout })));

// Marketing / legal
const LandingPage = lazy(() => import('./pages/LandingPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/legal/TermsOfServicePage'));
const CookiePolicyPage = lazy(() => import('./pages/legal/CookiePolicyPage'));

// Auth
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const SignupPage = lazy(() => import('./pages/auth/SignupPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const SecurityPage = lazy(() => import('./pages/account/SecurityPage'));

// Occupation — hub & browse
const OccupationHub = lazy(() => import('./pages/occupation/index'));
const BrowseOccupations = lazy(() => import('./pages/occupation/browse/index'));
const OccupationCategories = lazy(() => import('./pages/occupation/browse/categories/index'));
const CategoryDetail = lazy(() => import('./pages/occupation/browse/categories/[categoryId]'));
const TrendingOccupations = lazy(() => import('./pages/occupation/browse/trending'));
const RecommendedOccupations = lazy(() => import('./pages/occupation/browse/recommended'));
const OccupationSearch = lazy(() => import('./pages/occupation/browse/search'));

// Occupation — selection
const SelectionStart = lazy(() => import('./pages/occupation/selection/index'));
const AssessmentStart = lazy(() => import('./pages/occupation/selection/assessment/index'));
const AssessmentQuestions = lazy(() => import('./pages/occupation/selection/assessment/questions'));
const AssessmentResults = lazy(() => import('./pages/occupation/selection/assessment/results'));
const AssessmentRecommendations = lazy(() => import('./pages/occupation/selection/assessment/recommendations'));
const CompareOccupations = lazy(() => import('./pages/occupation/selection/compare'));
const OccupationDetails = lazy(() => import('./pages/occupation/selection/details/[id]'));

// Occupation — plans
const PlansOverview = lazy(() => import('./pages/occupation/plans/index'));
const PlansForOccupation = lazy(() => import('./pages/occupation/plans/[occupationId]'));
const ComparePlans = lazy(() => import('./pages/occupation/plans/compare'));
const PlanFeatures = lazy(() => import('./pages/occupation/plans/features'));

// Occupation — checkout
const CheckoutStart = lazy(() => import('./pages/occupation/checkout/index'));
const CheckoutPlanSelection = lazy(() => import('./pages/occupation/checkout/plan-selection'));
const CheckoutPaymentMethod = lazy(() => import('./pages/occupation/checkout/payment-method'));
const CheckoutReview = lazy(() => import('./pages/occupation/checkout/review'));
const CheckoutProcessing = lazy(() => import('./pages/occupation/checkout/processing'));
const CheckoutSuccess = lazy(() => import('./pages/occupation/checkout/success'));
const CheckoutFailed = lazy(() => import('./pages/occupation/checkout/failed'));
const CheckoutFreeTrial = lazy(() => import('./pages/occupation/checkout/free-trial'));

// Occupation — subscription
const SubscriptionOverview = lazy(() => import('./pages/occupation/subscription/index'));
const ActiveSubscriptions = lazy(() => import('./pages/occupation/subscription/active'));
const SubscriptionHistory = lazy(() => import('./pages/occupation/subscription/history'));
const UpgradeSubscription = lazy(() => import('./pages/occupation/subscription/upgrade'));
const CancelSubscription = lazy(() => import('./pages/occupation/subscription/cancel'));
const BillingInformation = lazy(() => import('./pages/occupation/subscription/billing'));

// Occupation — onboarding
const OnboardingStart = lazy(() => import('./pages/occupation/onboarding/index'));
const OnboardingWelcome = lazy(() => import('./pages/occupation/onboarding/welcome'));
const OnboardingSetup = lazy(() => import('./pages/occupation/onboarding/setup'));
const OnboardingGoals = lazy(() => import('./pages/occupation/onboarding/goals'));
const OnboardingComplete = lazy(() => import('./pages/occupation/onboarding/complete'));

// Career
const CareerDashboard = lazy(() => import('./pages/career/index'));
const JobSearchPage = lazy(() => import('./pages/career/search'));
const SavedJobs = lazy(() => import('./pages/career/saved'));
const Applications = lazy(() => import('./pages/career/applications'));
const JobsIndex = lazy(() => import('./pages/career/jobs/index'));
const JobDetail = lazy(() => import('./pages/career/jobs/[id]'));
const ApplyToJob = lazy(() => import('./pages/career/jobs/apply/[jobId]'));
const ApplySuccess = lazy(() => import('./pages/career/jobs/apply/success'));
const JobCategories = lazy(() => import('./pages/career/jobs/categories/index'));
const JobsByCategory = lazy(() => import('./pages/career/jobs/categories/[category]'));
const CompaniesIndex = lazy(() => import('./pages/career/companies/index'));
const CompanyDetail = lazy(() => import('./pages/career/companies/[id]'));
const FollowedCompanies = lazy(() => import('./pages/career/companies/following'));
const NetworkHome = lazy(() => import('./pages/career/network/index'));
const MyConnections = lazy(() => import('./pages/career/network/connections'));
const ConnectionRequests = lazy(() => import('./pages/career/network/requests'));
const SuggestedConnections = lazy(() => import('./pages/career/network/suggestions'));
const MessagesList = lazy(() => import('./pages/career/messages/index'));
const ChatThread = lazy(() => import('./pages/career/messages/[userId]'));
const InsightsDashboard = lazy(() => import('./pages/career/insights/index'));
const SkillsAnalysis = lazy(() => import('./pages/career/insights/skills'));
const MarketTrends = lazy(() => import('./pages/career/insights/market'));
const ProfileOverview = lazy(() => import('./pages/career/profile/index'));
const EditProfile = lazy(() => import('./pages/career/profile/edit'));
const ResumePage = lazy(() => import('./pages/career/profile/resume'));
const ProfileSkills = lazy(() => import('./pages/career/profile/skills'));
const ProfileExperience = lazy(() => import('./pages/career/profile/experience'));

// Membership & rewards (token system) — ADDED FOR TOKEN SYSTEM
const MembershipLayout = lazy(() =>
  import('./pages/(auth)/membership/MembershipLayout').then((m) => ({ default: m.MembershipLayout })),
);
const MembershipPricing = lazy(() => import('./pages/(auth)/membership/pricing'));
const TokenWallet = lazy(() => import('./pages/(auth)/membership/token-wallet'));
const RewardsDashboard = lazy(() => import('./pages/(auth)/membership/rewards'));
const FreeTrialIndex = lazy(() => import('./pages/(auth)/membership/free-trial/index'));
const TrialBonuses = lazy(() => import('./pages/(auth)/membership/free-trial/bonuses'));
const ConversionOffers = lazy(() => import('./pages/(auth)/membership/free-trial/conversion-offers'));
const ReferralDashboard = lazy(() => import('./pages/(auth)/membership/referral'));

// Admin (website administrators only) — ADDED FOR ADMIN SECTION
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const AdminOccupations = lazy(() => import('./pages/admin/AdminOccupations'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const AdminCategoryOccupations = lazy(() => import('./pages/admin/AdminCategoryOccupations'));
const StoreSettings = lazy(() => import('./pages/admin/store-settings')); // ADDED FOR REAL PAYMENT

/** Full-screen fallback shown while a lazy route chunk loads. */
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider> {/* ADDED FOR THEME */}
    <AuthProvider>
      <OccupationProvider>
        <SubscriptionProvider>
          <PaymentProvider>
            <OnboardingProvider>
              <MembershipProvider>
              <RewardsProvider>
              <Router>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public marketing & legal */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/careers" element={<CareersPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
                    <Route path="/legal/terms" element={<TermsOfServicePage />} />
                    <Route path="/legal/cookies" element={<CookiePolicyPage />} />

                    {/* Firebase email action handler (verify email / reset password).
                        Public on purpose — links must work whether or not signed in. */}
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* Auth pages — only for signed-out users */}
                    <Route element={<PublicOnlyRoute />}>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    </Route>

                    {/* Authenticated surfaces — sign-in required */}
                    <Route element={<ProtectedRoute />}>
                      {/* Email verification gate wraps the whole app surface */}
                      <Route element={<RequireVerifiedEmail />}>
                        {/* Account settings (sensitive — change pw / delete) */}
                        <Route path="/account/security" element={<SecurityPage />} />

                        {/* Admin (website administrators only — admin custom claim) */}
                        <Route element={<AdminRoute />}>
                          <Route path="/admin" element={<AdminLayout />}>
                            <Route index element={<Navigate to="/admin/occupations" replace />} />
                            <Route path="occupations" element={<AdminOccupations />} />
                            <Route path="categories" element={<AdminCategories />} />
                            <Route path="categories/:categoryId" element={<AdminCategoryOccupations />} />
                            <Route path="store-settings" element={<StoreSettings />} /> {/* ADDED FOR REAL PAYMENT */}
                          </Route>
                        </Route>

                        {/* Membership & rewards (token system) — ADDED FOR TOKEN SYSTEM */}
                        <Route path="/membership" element={<MembershipLayout />}>
                          <Route index element={<Navigate to="/membership/pricing" replace />} />
                          <Route path="pricing" element={<MembershipPricing />} />
                          <Route path="token-wallet" element={<TokenWallet />} />
                          <Route path="rewards" element={<RewardsDashboard />} />
                          <Route path="free-trial" element={<FreeTrialIndex />} />
                          <Route path="free-trial/bonuses" element={<TrialBonuses />} />
                          <Route path="free-trial/conversion-offers" element={<ConversionOffers />} />
                          <Route path="referral" element={<ReferralDashboard />} />
                        </Route>

                        {/* Occupation module */}
                        <Route path="/occupation" element={<OccupationLayout />}>
                          <Route index element={<OccupationHub />} />

                          {/* Browse */}
                          <Route path="browse" element={<BrowseOccupations />} />
                          <Route path="browse/categories" element={<OccupationCategories />} />
                          <Route path="browse/categories/:categoryId" element={<CategoryDetail />} />
                          <Route path="browse/trending" element={<TrendingOccupations />} />
                          <Route path="browse/recommended" element={<RecommendedOccupations />} />
                          <Route path="browse/search" element={<OccupationSearch />} />

                          {/* Selection */}
                          <Route path="selection" element={<SelectionStart />} />
                          <Route path="selection/assessment" element={<AssessmentStart />} />
                          <Route path="selection/assessment/questions" element={<AssessmentQuestions />} />
                          <Route path="selection/assessment/results" element={<AssessmentResults />} />
                          <Route path="selection/assessment/recommendations" element={<AssessmentRecommendations />} />
                          <Route path="selection/compare" element={<CompareOccupations />} />
                          <Route path="selection/details/:id" element={<OccupationDetails />} />

                          {/* Plans */}
                          <Route path="plans" element={<PlansOverview />} />
                          <Route path="plans/compare" element={<ComparePlans />} />
                          <Route path="plans/features" element={<PlanFeatures />} />
                          <Route path="plans/:occupationId" element={<PlansForOccupation />} />

                          {/* Checkout */}
                          <Route path="checkout" element={<CheckoutStart />} />
                          <Route path="checkout/plan-selection" element={<CheckoutPlanSelection />} />
                          <Route path="checkout/payment-method" element={<CheckoutPaymentMethod />} />
                          <Route path="checkout/review" element={<CheckoutReview />} />
                          <Route path="checkout/processing" element={<CheckoutProcessing />} />
                          <Route path="checkout/success" element={<CheckoutSuccess />} />
                          <Route path="checkout/failed" element={<CheckoutFailed />} />
                          <Route path="checkout/free-trial" element={<CheckoutFreeTrial />} />

                          {/* Subscription */}
                          <Route path="subscription" element={<SubscriptionOverview />} />
                          <Route path="subscription/active" element={<ActiveSubscriptions />} />
                          <Route path="subscription/history" element={<SubscriptionHistory />} />
                          <Route path="subscription/upgrade" element={<UpgradeSubscription />} />
                          <Route path="subscription/cancel" element={<CancelSubscription />} />
                          <Route path="subscription/billing" element={<BillingInformation />} />

                          {/* Onboarding */}
                          <Route path="onboarding" element={<OnboardingStart />} />
                          <Route path="onboarding/welcome" element={<OnboardingWelcome />} />
                          <Route path="onboarding/setup" element={<OnboardingSetup />} />
                          <Route path="onboarding/goals" element={<OnboardingGoals />} />
                          <Route path="onboarding/complete" element={<OnboardingComplete />} />
                        </Route>

                        {/* Career module — additionally gated by subscription inside CareerLayout */}
                        <Route path="/career" element={<CareerLayout />}>
                          <Route index element={<CareerDashboard />} />
                          <Route path="search" element={<JobSearchPage />} />
                          <Route path="saved" element={<SavedJobs />} />
                          <Route path="applications" element={<Applications />} />

                          {/* Jobs */}
                          <Route path="jobs" element={<JobsIndex />} />
                          <Route path="jobs/categories" element={<JobCategories />} />
                          <Route path="jobs/categories/:category" element={<JobsByCategory />} />
                          <Route path="jobs/apply/success" element={<ApplySuccess />} />
                          <Route path="jobs/apply/:jobId" element={<ApplyToJob />} />
                          <Route path="jobs/:id" element={<JobDetail />} />

                          {/* Companies */}
                          <Route path="companies" element={<CompaniesIndex />} />
                          <Route path="companies/following" element={<FollowedCompanies />} />
                          <Route path="companies/:id" element={<CompanyDetail />} />

                          {/* Network */}
                          <Route path="network" element={<NetworkHome />} />
                          <Route path="network/connections" element={<MyConnections />} />
                          <Route path="network/requests" element={<ConnectionRequests />} />
                          <Route path="network/suggestions" element={<SuggestedConnections />} />

                          {/* Messages */}
                          <Route path="messages" element={<MessagesList />} />
                          <Route path="messages/:userId" element={<ChatThread />} />

                          {/* Insights */}
                          <Route path="insights" element={<InsightsDashboard />} />
                          <Route path="insights/skills" element={<SkillsAnalysis />} />
                          <Route path="insights/market" element={<MarketTrends />} />

                          {/* Profile */}
                          <Route path="profile" element={<ProfileOverview />} />
                          <Route path="profile/edit" element={<EditProfile />} />
                          <Route path="profile/resume" element={<ResumePage />} />
                          <Route path="profile/skills" element={<ProfileSkills />} />
                          <Route path="profile/experience" element={<ProfileExperience />} />
                        </Route>
                      </Route>
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </Router>
              </RewardsProvider>
              </MembershipProvider>
            </OnboardingProvider>
          </PaymentProvider>
        </SubscriptionProvider>
      </OccupationProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}
